import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowLeft, Music, Star, Globe2 } from "lucide-react";
import StarGlow from "@/StarGlow";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TilloEpisodePage({ episodeId }) {
  const [ep, setEp] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [glow, setGlow] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/tillo/episodes/${episodeId}`)
      .then((r) => setEp(r.data))
      .catch(() => setNotFound(true));
  }, [episodeId]);

  const stanzas = (ep?.lyrics || "")
    .split(/\n\s*\n/)
    .map((s) => s.trim())
    .filter(Boolean);

  return (
    <div className="min-h-screen" style={{ background: "#FFF8EC", color: "#4A3B2A" }} data-testid="tillo-episode-page">
      <header className="px-6 py-5" style={{ background: "#FFEFD2" }}>
        <div className="max-w-[900px] mx-auto flex items-center justify-between">
          <a href="/kids" className="flex items-center gap-2 font-bold text-sm" style={{ color: "#3E6E64" }} data-testid="tillo-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to Tillo
          </a>
          <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: "#C98A2B" }}>Kids · Tillo the Storysaur</div>
        </div>
      </header>

      <main className="px-6 py-8 max-w-[900px] mx-auto">
        {notFound && (
          <div className="text-center py-20" data-testid="tillo-episode-not-found">
            <img src="/tillo.png" alt="Tillo" className="w-32 mx-auto mb-4 opacity-80" />
            <p className="font-display text-xl" style={{ color: "#3E6E64" }}>Oh no — Tillo couldn't find that adventure!</p>
            <a href="/kids" className="inline-block mt-4 px-6 py-2.5 rounded-full text-white font-bold" style={{ background: "#3E6E64" }}>See all adventures</a>
          </div>
        )}
        {ep && (
          <>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight" style={{ color: "#3E6E64" }} data-testid="tillo-episode-title">
              {ep.title}
            </h1>
            {ep.description && <p className="mt-3 text-base leading-relaxed max-w-2xl">{ep.description}</p>}

            <div className="mt-6 rounded-3xl overflow-hidden shadow-lg relative" style={{ border: "3px solid #FFE3A3", background: "#000" }}>
              {ep.video_url ? (
                <>
                  <video
                    src={`${process.env.REACT_APP_BACKEND_URL}${ep.video_url}`}
                    controls
                    className="w-full aspect-video bg-black"
                    poster={ep.thumbnail_url ? `${process.env.REACT_APP_BACKEND_URL}${ep.thumbnail_url}` : undefined}
                    onEnded={() => setGlow(true)}
                    data-testid="tillo-episode-video"
                  />
                  {glow && <StarGlow onClose={() => setGlow(false)} />}
                </>
              ) : (
                <div className="w-full aspect-video flex flex-col items-center justify-center gap-3" style={{ background: "#E8F4EF" }}>
                  <img src="/tillo.png" alt="Tillo" className="h-3/5 opacity-90" />
                  <div className="font-bold text-sm px-4 py-1.5 rounded-full" style={{ background: "#FFE3A3", color: "#9A6A14" }}>✨ Coming soon</div>
                </div>
              )}
            </div>

            {ep.video_slug && (
              <a
                href={`/s/${ep.video_slug}`}
                className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm transition-transform hover:scale-[1.02]"
                style={{ background: "#E8F4EF", color: "#3E6E64", border: "2px solid #BFE0D6" }}
                data-testid="tillo-episode-languages-link"
              >
                <Globe2 className="w-4 h-4" /> Watch this adventure in your language — 14 to choose from!
              </a>
            )}

            <section className="mt-10" data-testid="tillo-lyrics-section">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: "#FFE3A3" }}>
                  <Music className="w-5 h-5" style={{ color: "#9A6A14" }} />
                </div>
                <div>
                  <h2 className="font-display text-lg" style={{ color: "#3E6E64" }}>Sing along with Tillo!</h2>
                  {ep.song_title && (
                    <p className="text-sm font-semibold" style={{ color: "#C98A2B" }}>“{ep.song_title}”</p>
                  )}
                </div>
              </div>

              {stanzas.length > 0 ? (
                <div className="mt-5 rounded-3xl px-6 sm:px-10 py-8" style={{ background: "#FFFFFF", border: "3px solid #FFE3A3" }} data-testid="tillo-lyrics">
                  {stanzas.map((st, i) => (
                    <p key={i} className="text-lg sm:text-xl leading-loose font-semibold whitespace-pre-line mb-6 last:mb-0" style={{ color: "#4A3B2A" }}>
                      {st}
                    </p>
                  ))}
                  <div className="flex items-center gap-2 mt-6 text-sm font-bold" style={{ color: "#C98A2B" }}>
                    <Star className="w-4 h-4" fill="#FFD76A" color="#FFD76A" /> Every little voice can help something shine!
                  </div>
                </div>
              ) : (
                <p className="mt-4 text-sm italic" style={{ color: "#8A7A5E" }} data-testid="tillo-lyrics-empty">
                  The sing-along words for this adventure are on their way — check back soon!
                </p>
              )}
            </section>
          </>
        )}
      </main>

      <footer className="px-6 py-8 text-center text-xs" style={{ color: "#8A7A5E" }}>
        Tillo the Storysaur · part of <a href="/" className="underline">Storylo</a>
      </footer>
    </div>
  );
}
